import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBelongsToFirm } from "../../hooks/useFirmMembership";
import { useInPaidBusiness, useOwnsBusiness, useReadsUnpaidBusiness } from "../../hooks/useInPaidBusiness";
import { useFieldStaff } from "../../hooks/useFieldStaff";
import { PaywallModal } from "../subscription/PaywallModal";
import { isBusinessRoute, paywallDecision } from "./paywallScope";
import { isDemo } from "../../config/demo";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const FullPageLoading: React.FC = () => (
  <div style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#FFFFFF",
    color: "#000000",
    fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    fontSize: "16px"
  }}>
    Loading...
  </div>
);

/**
 * The paywall, for a signed-in user without Pro. Its own component so the
 * membership questions (a practice? a paying business?) are only asked once
 * somebody is signed in, and never of a Pro subscriber: asked during the auth
 * load, the membership store would be cleared and re-fetched on every page
 * load for everyone.
 */
const PaywallGate: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { pathname } = useLocation();
  const belongsToFirm = useBelongsToFirm();
  const inPaidBusiness = useInPaidBusiness();
  // PRD 89 §4.14: a technician or dispatcher anywhere meets no paywall on the
  // business screens. Same request as inPaidBusiness, so no extra wait.
  const { fieldStaff } = useFieldStaff();
  // Dave's decision 11 (2026-10-09): an owner reaches their billing card. Same
  // request again.
  const { ownsBusiness, unpaidOwnedBusinessId } = useOwnsBusiness();
  // G46: a clerk or read-only member of an unpaid business keeps reading it.
  const { readsUnpaidBusiness } = useReadsUnpaidBusiness();
  const decision = paywallDecision({
    isPro: false,
    pathname,
    belongsToFirm,
    inPaidBusiness,
    fieldStaff,
    ownsBusiness,
    readsUnpaidBusiness,
  });

  if (decision === "wait") return <FullPageLoading />;

  return (
    <>
      {children}
      {decision === "paywall" && (
        <PaywallModal
          belongsToFirm={belongsToFirm === true}
          // Either way the books are theirs to open, so the paywall offers the
          // way back; but an owner of an unpaid business is shown its bill first.
          inPaidBusiness={inPaidBusiness === true || (readsUnpaidBusiness === true && !unpaidOwnedBusinessId)}
          unpaidOwnedBusinessId={unpaidOwnedBusinessId}
        />
      )}
    </>
  );
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, subscriptionLoading, isBeta, isPro } = useAuth();
  const location = useLocation();
  // PRD 89 §4.14: field staff skip the waitlist on /business/*, for
  // ClaimService's reason — a paying customer invited them into a specific
  // business, and a beta flag on an existing account must not strand them.
  // And on '/', where every sign-in lands: LandingRedirect's field rung
  // (§4.12) sends them on to their own page, and anyone it sends to a
  // personal screen meets the waitlist there. Asked only of a waitlisted user
  // on one of those paths.
  const fieldExemptPath = isBusinessRoute(location.pathname) || location.pathname === "/";
  const { fieldStaff } = useFieldStaff(
    !loading && !subscriptionLoading && Boolean(user?.authToken) && isBeta && fieldExemptPath
  );

  if (loading || subscriptionLoading) {
    return <FullPageLoading />;
  }

  if (!user?.authToken) {
    // The Live sample has no login page: signed out there means the session
    // ended, and the way back in is a fresh sample (PRD 117, D3).
    if (isDemo()) return <Navigate to="/demo/ended" replace />;
    // Keep where they were going, so a link to a signed-in page (the
    // website's "Set up your practice" is one) lands there after sign-in
    // rather than on the dashboard. LoginForm validates return_to.
    const destination = `${location.pathname}${location.search}`;
    const to = destination === "/" ? "/login" : `/login?return_to=${encodeURIComponent(destination)}`;
    return <Navigate to={to} replace />;
  }

  if (isBeta) {
    if (fieldExemptPath && fieldStaff === null) return <FullPageLoading />;
    if (!(fieldExemptPath && fieldStaff)) return <Navigate to="/waitlist" replace />;
  }

  if (isPro) return <>{children}</>;

  return <PaywallGate>{children}</PaywallGate>;
};
